import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import styled from "@emotion/styled";
import {carsTheme} from "@rw/themes";

import {fetchCitiesByStateName} from "../app/api/fetch_cities";
import {apiLink} from "../app/routes/api_link";
import {getStateNameBySlug} from "../app/types/locationTypes";
import {appendQueryString} from "../app/utils/append_query_string";
import {getRequest} from "../app/utils/request_response_utils/request";
import {City} from "../offer/list/actions/fetch_location_car_list_data_at_route";
import {ClearableSelect} from "./ClearableSelect";
import {FilterFieldClear, InputWrap} from "./FilterFieldClear";
import {
    CityFilterOption,
    VOIVODESHIP_FILTER_OPTIONS,
    mergeCityOptions,
    toCitySlug
} from "./location_filter_utils";
import {SearchFormValues} from "./search_filter_config";

interface RegionCitiesResponse {
    region?: string;
    cities?: City[];
}

interface ISearchLocationFieldsProps {
    variant: string;
    values: SearchFormValues;
    setValues: (values: SearchFormValues) => void;
    /** Prefer Polish city label from SSR when available. */
    initialCityLabel?: string;
}

export const SearchLocationFields: React.FC<ISearchLocationFieldsProps> = ({
    variant,
    values,
    setValues,
    initialCityLabel
}) => {
    const [cityOptions, setCityOptions] = useState<CityFilterOption[]>([]);
    const [cityQuery, setCityQuery] = useState(initialCityLabel || values.subLocation || "");
    const [isCityOpen, setIsCityOpen] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const loadIdRef = useRef(0);
    const valuesRef = useRef(values);
    valuesRef.current = values;

    const selectedCityLabel = useMemo(() => {
        if (!values.subLocation) {
            return "";
        }
        return cityOptions.find((c) => c.value === values.subLocation)?.label || initialCityLabel || values.subLocation;
    }, [cityOptions, values.subLocation, initialCityLabel]);

    useEffect(() => {
        if (!values.subLocation) {
            setCityQuery("");
            return;
        }
        setCityQuery(selectedCityLabel);
    }, [values.subLocation, selectedCityLabel]);

    useEffect(() => {
        if (!values.location) {
            setCityOptions([]);
            setIsLoadingCities(false);
            return;
        }

        const loadId = ++loadIdRef.current;
        setIsLoadingCities(true);

        const stateName = getStateNameBySlug(values.location);
        const offersUrl = appendQueryString(apiLink.voivodeshipLocations({})(null), {
            wojew: values.location,
            ...(values.make ? {type: values.make} : {})
        });

        Promise.all([
            getRequest({}, offersUrl, "fetchSearchVoivodeshipCities")
                .then((json: RegionCitiesResponse) => json?.cities || [])
                .catch(() => [] as City[]),
            stateName ? fetchCitiesByStateName(stateName) : Promise.resolve([] as string[])
        ])
            .then(([offerCities, allNames]) => {
                if (loadId !== loadIdRef.current) {
                    return;
                }
                const withOffers: CityFilterOption[] = offerCities.map((city) => ({
                    label: city.city,
                    value: city.citySlug || toCitySlug(city.city),
                    offerCount: city.offer_count
                }));
                setCityOptions(mergeCityOptions(withOffers, allNames));
            })
            .finally(() => {
                if (loadId === loadIdRef.current) {
                    setIsLoadingCities(false);
                }
            });
    }, [values.location, values.make]);

    useEffect(() => {
        const onDocMouseDown = (event: MouseEvent) => {
            if (!wrapRef.current?.contains(event.target as Node)) {
                setIsCityOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, []);

    const filteredCities = useMemo(() => {
        const q = cityQuery.trim().toLocaleLowerCase("pl");
        const list = q
            ? cityOptions.filter(
                  (city) => city.label.toLocaleLowerCase("pl").includes(q) || city.value.includes(toCitySlug(q))
              )
            : cityOptions;
        return list.slice(0, 40);
    }, [cityOptions, cityQuery]);

    const handleVoivodeshipChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setValues({
            ...valuesRef.current,
            location: event.target.value,
            subLocation: ""
        });
        setCityQuery("");
        setIsCityOpen(false);
    };

    const pickCity = (city: CityFilterOption) => {
        setValues({
            ...valuesRef.current,
            subLocation: city.value
        });
        setCityQuery(city.label);
        setIsCityOpen(false);
    };

    const cityQueryRef = useRef(cityQuery);
    cityQueryRef.current = cityQuery;

    const handleCityInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setCityQuery(event.target.value);
        setIsCityOpen(true);
    };

    const handleCityBlur = () => {
        window.setTimeout(() => {
            const current = valuesRef.current;
            if (!current.location) {
                return;
            }
            const trimmed = cityQueryRef.current.trim();
            if (!trimmed) {
                if (current.subLocation) {
                    setValues({...current, subLocation: ""});
                }
                setCityQuery("");
                return;
            }
            const exact = cityOptions.find((c) => c.label.toLocaleLowerCase("pl") === trimmed.toLocaleLowerCase("pl"));
            if (exact) {
                if (current.subLocation !== exact.value) {
                    setValues({...current, subLocation: exact.value});
                }
                setCityQuery(exact.label);
                return;
            }
            const slug = toCitySlug(trimmed);
            if (slug && current.subLocation !== slug) {
                setValues({...current, subLocation: slug});
            }
        }, 120);
    };

    const clearCity = () => {
        setCityQuery("");
        setIsCityOpen(false);
        if (valuesRef.current.subLocation) {
            setValues({...valuesRef.current, subLocation: ""});
        }
    };

    return (
        <>
            <FormControl>
                <Label htmlFor={`location-${variant}`}>Województwo</Label>
                <ClearableSelect
                    id={`location-${variant}`}
                    name="location"
                    value={values.location}
                    options={VOIVODESHIP_FILTER_OPTIONS}
                    placeholder="Cała Polska"
                    clearLabel="Wyczyść województwo"
                    useFormikField={false}
                    onChange={handleVoivodeshipChange}
                    onClear={() => {
                        setValues({
                            ...valuesRef.current,
                            location: "",
                            subLocation: ""
                        });
                        setCityQuery("");
                        setIsCityOpen(false);
                    }}
                />
            </FormControl>

            <FormControl ref={wrapRef}>
                <Label htmlFor={`subLocation-${variant}`}>Miejscowość</Label>
                {values.subLocation ? (
                    <InputWrap data-clearable="1">
                        <SelectedCity title={selectedCityLabel}>{selectedCityLabel}</SelectedCity>
                        <FilterFieldClear visible label="Wyczyść miejscowość" onClear={clearCity} />
                    </InputWrap>
                ) : (
                    <InputWrap>
                        <CityInput
                            id={`subLocation-${variant}`}
                            name="subLocationDisplay"
                            type="text"
                            autoComplete="off"
                            placeholder={
                                values.location
                                    ? isLoadingCities
                                        ? "Ładuję miasta…"
                                        : "Wpisz miasto"
                                    : "Najpierw wybierz województwo"
                            }
                            value={cityQuery}
                            disabled={!values.location || isLoadingCities}
                            onChange={handleCityInputChange}
                            onFocus={() => values.location && setIsCityOpen(true)}
                            onBlur={handleCityBlur}
                        />
                    </InputWrap>
                )}
                {!values.subLocation && isCityOpen && values.location && filteredCities.length > 0 ? (
                    <CityDropdown role="listbox">
                        {filteredCities.map((city) => (
                            <CityOption
                                key={city.value}
                                type="button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    pickCity(city);
                                }}
                            >
                                <span>{city.label}</span>
                                {typeof city.offerCount === "number" ? <CityCount>{city.offerCount}</CityCount> : null}
                            </CityOption>
                        ))}
                    </CityDropdown>
                ) : null}
            </FormControl>
        </>
    );
};

const FormControl = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 0;
`;

const Label = styled.label`
    margin-bottom: 5px;
    color: ${carsTheme.colors.gray};
    font-size: 14px;
`;

const CityInput = styled.input`
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 8px;
    font-size: 16px;
    border: 2px solid ${carsTheme.colors.gray_light};
    border-radius: 4px;
    background: #fff;
    color: ${carsTheme.colors.gray};

    &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
    }
`;

const SelectedCity = styled.div`
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 8px 34px 8px 8px;
    font-size: 16px;
    border: 2px solid ${carsTheme.colors.gray_light};
    border-radius: 4px;
    background: #fff;
    color: ${carsTheme.colors.brand_black};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CityDropdown = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 30;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid ${carsTheme.colors.gray_light};
    border-radius: 4px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
`;

const CityOption = styled.button`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border: 0;
    background: #fff;
    text-align: left;
    font-size: 14px;
    color: ${carsTheme.colors.brand_black};
    cursor: pointer;

    &:hover {
        background: #f6f6f6;
    }
`;

const CityCount = styled.span`
    color: ${carsTheme.colors.gray};
    font-size: 12px;
`;
